import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Renders app markup as a picture on the marketing page (the homepage monitor and "See both
 * routes"): `<div id="app">` inside a shadow root on the `.appmini` host.
 *
 * Why a shadow root: the site's band rules are more specific than the app's (`#site .band.stdark
 * p` beats `#app .card p`), so in the light bands they recoloured the app's text. The prototype
 * (v33) fixed that by rewriting every copied rule to `#site .appmini.appmini`; here the preview
 * simply sits outside the site's cascade. Only the app's own rules (those naming `#app`, plus
 * keyframes and `*`) are copied in, so `:root`-based dark-mode rules never match and the preview
 * always shows the app's morning theme; its tokens come from `#site .appmini` (site.css), which
 * the shadow tree inherits. The app's fixed sunrise dome is hidden: fixed to the viewport, it
 * means nothing inside a scaled fragment.
 */
export function AppFrame({
  className = "appmini",
  frameRef,
  extraCss = "",
  children,
}: {
  className?: string;
  frameRef?: React.Ref<HTMLDivElement>;
  /** rules for this frame only, e.g. dropping a card's border */
  extraCss?: string;
  children: ReactNode;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const [root, setRoot] = useState<ShadowRoot | null>(null);

  useLayoutEffect(() => {
    const h = host.current;
    if (!h || !h.attachShadow) return;
    const sr = h.shadowRoot ?? h.attachShadow({ mode: "open" });
    let style = sr.querySelector("style");
    if (!style) {
      style = document.createElement("style");
      sr.prepend(style);
    }
    style.textContent = appCss() + extraCss;
    setRoot(sr);
  }, [extraCss]);

  const setHost = (el: HTMLDivElement | null) => {
    host.current = el;
    if (typeof frameRef === "function") frameRef(el);
    else if (frameRef) (frameRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
  };

  return (
    <div className={className} ref={setHost}>
      {root && createPortal(<div id="app">{children}</div>, root)}
    </div>
  );
}

/** The base the app takes from `body`, which isn't an `#app` rule; and no dome in a picture. */
const RESET = `
#app{color:var(--ink);font-family:Inter,system-ui,-apple-system,"Segoe UI",sans-serif;
  font-size:15px;line-height:1.55;text-align:left;letter-spacing:normal;-webkit-font-smoothing:antialiased}
#app::before{display:none}
`;

let cache: { n: number; css: string } | null = null;

/** The document's `#app` rules as text, cached until a stylesheet is added. */
export function appCss(): string {
  // Only the document's own sheets: never another frame's shadow-root copy.
  const sheets = [...document.styleSheets].filter((s) => s.ownerNode?.getRootNode() === document);
  if (cache && cache.n === sheets.length) return cache.css;
  const out: string[] = [];
  for (const sheet of sheets) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // a cross-origin sheet (fonts): nothing of the app's in it
    }
    for (const rule of rules) {
      const text = rule.cssText;
      if (
        text.includes("#app") ||
        text.startsWith("@keyframes") ||
        (rule as CSSStyleRule).selectorText === "*"
      ) {
        out.push(text);
      }
    }
  }
  cache = { n: sheets.length, css: out.join("\n") + RESET };
  return cache.css;
}
